import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  sendLookupOtp,
  verifyLookupOtp,
  setLookupToken,
  getMyContracts,
  getContractToken,
  clearLookupToken,
  setAuthToken,
  getAuthToken,
  getLookupFromSession,
} from "../../services";
import {
  Alert,
  Spinner,
  FormGroup,
  FormLabel,
} from "../../styles/SharedStyles";
import { TEXTS, formatText } from "../../constants";
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  CloseButton,
  ModalTitle,
  ModalSubtitle,
  PhoneInputWrapper,
  PhonePrefix,
  PhoneInput,
  ModernButton,
  OtpContainer,
  OtpInputs,
  OtpInput,
  ResendLink,
  ContractsList,
  ContractCard,
  ContractHeader,
  ContractName,
  StatusBadge,
  ContractDetails,
  DetailItem,
  LoadingContainer,
  LoadingText,
} from "./DocumentLookup.styles";

function DocumentLookup({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("checking"); // 'checking', 'phone', 'otp', 'contracts'
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loadingContract, setLoadingContract] = useState(null);

  const otpRefs = useRef([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Check for existing session when modal opens
  useEffect(() => {
    if (isOpen) {
      // Check if user already has a valid signing session
      const authToken = getAuthToken();
      if (authToken) {
        // Try to get lookup token from existing session (no OTP needed)
        setStep("checking");
        checkExistingSession();
      } else {
        // No existing session, show phone input
        setStep("phone");
      }
    } else {
      // Reset state when modal closes
      setStep("checking");
      setPhone("");
      setOtp(["", "", "", "", "", ""]);
      setError(null);
      setDevOtp(null);
      setContracts([]);
      clearLookupToken();
    }
  }, [isOpen]);

  // Check if user has existing session and skip OTP
  const checkExistingSession = async () => {
    try {
      setError(null);

      // Get lookup token from signing session
      const response = await getLookupFromSession();
      const { token, phone: sessionPhone } = response.data;

      // Save lookup token
      setLookupToken(token);
      setPhone(sessionPhone);

      // Load contracts directly
      await loadContracts();
      setStep("contracts");
    } catch (err) {
      // Session invalid or expired, show normal OTP flow
      console.log("No valid session, showing OTP flow");
      setStep("phone");
    }
  };

  // Handle phone submission
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate phone
    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length < 9 || cleanPhone.length > 10) {
      setError(TEXTS.LOOKUP.ERRORS.INVALID_PHONE);
      return;
    }

    try {
      setLoading(true);
      const response = await sendLookupOtp(cleanPhone);

      // Development mode - show OTP
      if (response.data._devOtp) {
        setDevOtp(response.data._devOtp);
      }

      setStep("otp");
      setResendCooldown(60);
    } catch (err) {
      setError(err.message || TEXTS.LOOKUP.ERRORS.SEND_FAILED);
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP submission
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError(TEXTS.LOOKUP.ERRORS.INCOMPLETE_OTP);
      return;
    }

    try {
      setLoading(true);
      const cleanPhone = phone.replace(/\D/g, "");
      const response = await verifyLookupOtp(cleanPhone, otpCode);

      // Save JWT token
      setLookupToken(response.data.token);

      // Load contracts
      await loadContracts();

      setStep("contracts");
    } catch (err) {
      setError(err.message || TEXTS.LOOKUP.ERRORS.INVALID_OTP);
      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Load contracts
  const loadContracts = async () => {
    try {
      const response = await getMyContracts();
      setContracts(response.data.contracts || []);
    } catch (err) {
      setError(err.message || TEXTS.LOOKUP.ERRORS.LOAD_FAILED);
    }
  };

  // Handle resend
  const handleResend = async () => {
    if (resendCooldown > 0) return;

    try {
      setLoading(true);
      setError(null);
      const cleanPhone = phone.replace(/\D/g, "");
      const response = await sendLookupOtp(cleanPhone);

      if (response.data._devOtp) {
        setDevOtp(response.data._devOtp);
      }

      setOtp(["", "", "", "", "", ""]);
      setResendCooldown(60);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || TEXTS.LOOKUP.ERRORS.SEND_FAILED);
    } finally {
      setLoading(false);
    }
  };

  // Handle contract selection
  const handleContractSelect = async (contractId) => {
    try {
      setLoadingContract(contractId);
      setError(null);

      const response = await getContractToken(contractId);
      const { token, signingToken } = response.data;

      // Save the signing token - this will skip OTP on the sign page
      if (signingToken) {
        setAuthToken(signingToken);
      }

      // Navigate to sign page
      onClose();
      navigate(`/sign/${token}`);
    } catch (err) {
      setError(err.message || TEXTS.LOOKUP.ERRORS.TOKEN_FAILED);
    } finally {
      setLoadingContract(null);
    }
  };

  // Format resend countdown text
  const getResendText = () => {
    if (resendCooldown > 0) {
      return formatText(TEXTS.LOOKUP.OTP_RESEND_COUNTDOWN, {
        seconds: resendCooldown,
      });
    }
    return TEXTS.LOOKUP.OTP_RESEND;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Format price
  const formatPrice = (price, currency) => {
    if (!price) return null;
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: currency || "ILS",
    }).format(price);
  };

  // Get status text
  const getStatusText = (status) => {
    return TEXTS.LOOKUP.STATUS[status] || status;
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <CloseButton onClick={onClose}>&times;</CloseButton>

        {step === "checking" && (
          <ModalBody
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingContainer>
              <Spinner size="32px" />
              <LoadingText>בודק הפעלה קיימת...</LoadingText>
            </LoadingContainer>
          </ModalBody>
        )}

        {step === "phone" && (
          <>
            <ModalHeader>
              <ModalTitle>{TEXTS.LOOKUP.TITLE}</ModalTitle>
              <ModalSubtitle>{TEXTS.LOOKUP.SUBTITLE}</ModalSubtitle>
            </ModalHeader>

            <ModalBody>
              <form onSubmit={handlePhoneSubmit}>
                <FormGroup>
                  <FormLabel>{TEXTS.LOOKUP.PHONE_LABEL}</FormLabel>
                  <PhoneInputWrapper>
                    <PhonePrefix>{TEXTS.LOOKUP.PHONE_PREFIX}</PhonePrefix>
                    <PhoneInput
                      type="tel"
                      placeholder={TEXTS.LOOKUP.PHONE_PLACEHOLDER}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={loading}
                      autoComplete="tel"
                      dir="ltr"
                      autoFocus
                    />
                  </PhoneInputWrapper>
                </FormGroup>

                {error && <Alert variant="error">{error}</Alert>}

                <ModernButton type="submit" disabled={loading || !phone}>
                  {loading ? (
                    <Spinner size="20px" color="white" />
                  ) : (
                    TEXTS.LOOKUP.SEND_OTP
                  )}
                </ModernButton>
              </form>
            </ModalBody>
          </>
        )}

        {step === "otp" && (
          <>
            <ModalHeader>
              <ModalTitle>{TEXTS.LOOKUP.OTP_TITLE}</ModalTitle>
              <ModalSubtitle>
                {TEXTS.LOOKUP.OTP_SENT_TO} {phone}
              </ModalSubtitle>
            </ModalHeader>

            <ModalBody>
              {devOtp && (
                <Alert variant="info" style={{ marginBottom: "16px" }}>
                  <strong>{TEXTS.LOOKUP.OTP_DEV_MODE}</strong>{" "}
                  {TEXTS.LOOKUP.OTP_CODE_LABEL} {devOtp}
                </Alert>
              )}

              <form onSubmit={handleOtpSubmit}>
                <OtpContainer>
                  <OtpInputs onPaste={handleOtpPaste}>
                    {otp.map((digit, index) => (
                      <OtpInput
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        $filled={!!digit}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    ))}
                  </OtpInputs>
                </OtpContainer>

                {error && (
                  <Alert variant="error" style={{ marginTop: "16px" }}>
                    {error}
                  </Alert>
                )}

                <ModernButton
                  type="submit"
                  disabled={loading || otp.some((d) => !d)}
                  $marginTop="24px"
                >
                  {loading ? (
                    <Spinner size="20px" color="white" />
                  ) : (
                    TEXTS.LOOKUP.OTP_VERIFY
                  )}
                </ModernButton>

                <ResendLink>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || loading}
                  >
                    {getResendText()}
                  </button>
                </ResendLink>

                <ModernButton
                  type="button"
                  $secondary
                  onClick={() => {
                    setStep("phone");
                    setOtp(["", "", "", "", "", ""]);
                    setError(null);
                  }}
                  disabled={loading}
                  $marginTop="12px"
                >
                  {TEXTS.LOOKUP.BACK}
                </ModernButton>
              </form>
            </ModalBody>
          </>
        )}

        {step === "contracts" && (
          <>
            <ModalHeader>
              <ModalTitle>{TEXTS.LOOKUP.CONTRACTS_TITLE}</ModalTitle>
              <ModalSubtitle>
                {contracts.length > 0
                  ? formatText(TEXTS.LOOKUP.CONTRACTS_SUBTITLE, {
                      count: contracts.length,
                    })
                  : TEXTS.LOOKUP.NO_CONTRACTS}
              </ModalSubtitle>
            </ModalHeader>

            <ModalBody>
              {error && <Alert variant="error">{error}</Alert>}

              {loading ? (
                <LoadingContainer>
                  <Spinner size="32px" />
                  <LoadingText>{TEXTS.COMMON.LOADING}</LoadingText>
                </LoadingContainer>
              ) : (
                <ContractsList>
                  {contracts.map((contract) => (
                    <ContractCard key={contract.id}>
                      <ContractHeader>
                        <ContractName>{contract.clientName}</ContractName>
                        <StatusBadge $status={contract.status}>
                          {getStatusText(contract.status)}
                        </StatusBadge>
                      </ContractHeader>

                      <ContractDetails>
                        <DetailItem>
                          <span>{TEXTS.LOOKUP.CONTRACT_CARD.TEMPLATE}</span>
                          <span>{contract.templateName}</span>
                        </DetailItem>
                        <DetailItem>
                          <span>{TEXTS.LOOKUP.CONTRACT_CARD.EXPIRES}</span>
                          <span>{formatDate(contract.expiresAt)}</span>
                        </DetailItem>
                        {contract.serviceName && (
                          <DetailItem>
                            <span>{TEXTS.LOOKUP.CONTRACT_CARD.SERVICE}</span>
                            <span>{contract.serviceName}</span>
                          </DetailItem>
                        )}
                        {contract.price && (
                          <DetailItem>
                            <span>{TEXTS.LOOKUP.CONTRACT_CARD.PRICE}</span>
                            <span>
                              {formatPrice(contract.price, contract.currency)}
                            </span>
                          </DetailItem>
                        )}
                      </ContractDetails>

                      <ModernButton
                        onClick={() => handleContractSelect(contract.id)}
                        disabled={loadingContract === contract.id}
                      >
                        {loadingContract === contract.id ? (
                          <Spinner size="20px" color="white" />
                        ) : (
                          TEXTS.LOOKUP.CONTRACT_CARD.SIGN_NOW
                        )}
                      </ModernButton>
                    </ContractCard>
                  ))}
                </ContractsList>
              )}

              <ModernButton
                type="button"
                $secondary
                onClick={onClose}
                $marginTop="24px"
              >
                {TEXTS.LOOKUP.CLOSE}
              </ModernButton>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}

export default DocumentLookup;
